import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { NetworkError, NetworkErrorType } from '@/types/api'
import { ErrorHandler } from '@/utils/errorHandler'

/**
 * 错误信息接口
 */
export interface ErrorInfo {
  id: string
  type: NetworkErrorType
  message: string
  suggestion?: string
  timestamp: number
  context?: string
  retryable: boolean
  retryCount: number
  maxRetries: number
}

/**
 * 加载状态接口
 */
export interface LoadingState {
  id: string
  message: string
  progress?: number
  timestamp: number
  context?: string
}

/**
 * 网络状态接口
 */
export interface NetworkStatus {
  isOnline: boolean
  lastOnlineTime?: number
  connectionType?: string
  effectiveType?: string
}

/**
 * 全局错误处理和加载状态管理 Store
 */
export const useErrorHandlerStore = defineStore('errorHandler', () => {
  // 状态
  const errors = ref<Map<string, ErrorInfo>>(new Map())
  const loadingStates = ref<Map<string, LoadingState>>(new Map())
  const networkStatus = ref<NetworkStatus>({ isOnline: navigator.onLine })
  const globalLoading = ref(false)
  const retryQueue = ref<Map<string, () => Promise<any>>>(new Map())

  // 计算属性
  const hasErrors = computed(() => errors.value.size > 0)
  const hasLoading = computed(() => loadingStates.value.size > 0 || globalLoading.value)
  const errorCount = computed(() => errors.value.size)
  const loadingCount = computed(() => loadingStates.value.size)
  
  const criticalErrors = computed(() => 
    Array.from(errors.value.values()).filter(error => 
      error.type === 'AUTH_ERROR' || error.type === 'SERVER_ERROR'
    )
  )
  
  const networkErrors = computed(() => 
    Array.from(errors.value.values()).filter(error => 
      error.type === 'CONNECTION_ERROR' || error.type === 'TIMEOUT'
    )
  )

  const retryableErrors = computed(() => 
    Array.from(errors.value.values()).filter(error => error.retryable)
  )

  // 生成唯一ID
  const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  // 错误处理方法
  const addError = (
    error: NetworkError, 
    context?: string, 
    retryFn?: () => Promise<any>
  ): string => {
    const id = generateId()
    const isRetryable = ErrorHandler.isNetworkError(error) || ErrorHandler.isServerError(error)
    
    const errorInfo: ErrorInfo = {
      id,
      type: error.type,
      message: ErrorHandler.getErrorMessage(error),
      suggestion: ErrorHandler.getErrorSuggestion(error),
      timestamp: Date.now(),
      context,
      retryable: isRetryable,
      retryCount: 0,
      maxRetries: isRetryable ? 3 : 0
    }

    errors.value.set(id, errorInfo)

    // 如果提供了重试函数，添加到重试队列
    if (retryFn && isRetryable) {
      retryQueue.value.set(id, retryFn)
    }

    // 记录错误日志
    ErrorHandler.logError(error, context)

    // 自动清理错误（30秒后）
    setTimeout(() => {
      removeError(id)
    }, 30000)

    return id
  }

  const removeError = (id: string): void => {
    errors.value.delete(id)
    retryQueue.value.delete(id)
  }

  const clearErrors = (): void => {
    errors.value.clear()
    retryQueue.value.clear()
  }

  const clearErrorsByType = (type: NetworkErrorType): void => {
    for (const [id, error] of errors.value.entries()) {
      if (error.type === type) {
        removeError(id)
      }
    }
  }

  const clearErrorsByContext = (context: string): void => {
    for (const [id, error] of errors.value.entries()) {
      if (error.context === context) {
        removeError(id)
      }
    }
  }

  // 重试错误
  const retryError = async (id: string): Promise<boolean> => {
    const error = errors.value.get(id)
    const retryFn = retryQueue.value.get(id)

    if (!error || !retryFn || !error.retryable) {
      return false
    }

    if (error.retryCount >= error.maxRetries) {
      console.warn(`[ErrorHandler] 错误 ${id} 已达到最大重试次数`)
      return false
    }

    try {
      // 更新重试计数
      error.retryCount++
      errors.value.set(id, { ...error })

      // 执行重试
      await retryFn()
      
      // 重试成功，移除错误
      removeError(id)
      return true
    } catch (retryError) {
      console.error(`[ErrorHandler] 重试失败:`, retryError)
      
      // 如果还有重试机会，保留错误；否则标记为不可重试
      if (error.retryCount >= error.maxRetries) {
        error.retryable = false
        errors.value.set(id, { ...error })
      }
      
      return false
    }
  }

  // 重试所有可重试的错误
  const retryAllErrors = async (): Promise<void> => {
    const retryableErrorIds = Array.from(errors.value.entries())
      .filter(([_, error]) => error.retryable && error.retryCount < error.maxRetries)
      .map(([id]) => id)

    const retryPromises = retryableErrorIds.map(id => retryError(id))
    await Promise.allSettled(retryPromises)
  }

  // 加载状态管理方法
  const startLoading = (message: string, context?: string): string => {
    const id = generateId()
    const loadingState: LoadingState = {
      id,
      message,
      timestamp: Date.now(),
      context
    }

    loadingStates.value.set(id, loadingState)
    return id
  }

  const updateLoading = (id: string, message?: string, progress?: number): void => {
    const loadingState = loadingStates.value.get(id)
    if (loadingState) {
      if (message !== undefined) loadingState.message = message
      if (progress !== undefined) loadingState.progress = progress
      loadingStates.value.set(id, { ...loadingState })
    }
  }

  const stopLoading = (id: string): void => {
    loadingStates.value.delete(id)
  }

  const clearLoading = (): void => {
    loadingStates.value.clear()
  }

  const clearLoadingByContext = (context: string): void => {
    for (const [id, loading] of loadingStates.value.entries()) {
      if (loading.context === context) {
        stopLoading(id)
      }
    }
  }

  // 全局加载状态
  const setGlobalLoading = (loading: boolean): void => {
    globalLoading.value = loading
  }

  // 网络状态管理
  const updateNetworkStatus = (status: Partial<NetworkStatus>): void => {
    networkStatus.value = { ...networkStatus.value, ...status }
  }

  const setOnlineStatus = (isOnline: boolean): void => {
    const wasOffline = !networkStatus.value.isOnline
    networkStatus.value.isOnline = isOnline
    
    if (isOnline) {
      networkStatus.value.lastOnlineTime = Date.now()
      
      // 如果从离线状态恢复，清除网络相关错误并重试
      if (wasOffline) {
        console.log('[ErrorHandler] 网络连接恢复，清除网络错误并重试')
        clearErrorsByType('CONNECTION_ERROR')
        clearErrorsByType('TIMEOUT')
        
        // 延迟重试，给网络一些时间稳定
        setTimeout(() => {
          retryAllErrors()
        }, 1000)
      }
    }
  }

  // 获取网络连接信息
  const getConnectionInfo = (): NetworkStatus => {
    const connection = (navigator as any).connection || 
                     (navigator as any).mozConnection || 
                     (navigator as any).webkitConnection

    if (connection) {
      return {
        ...networkStatus.value,
        connectionType: connection.type,
        effectiveType: connection.effectiveType
      }
    }

    return networkStatus.value
  }

  // 工具方法
  const getErrorById = (id: string): ErrorInfo | undefined => {
    return errors.value.get(id)
  }

  const getLoadingById = (id: string): LoadingState | undefined => {
    return loadingStates.value.get(id)
  }

  const getErrorsByContext = (context: string): ErrorInfo[] => {
    return Array.from(errors.value.values()).filter(error => error.context === context)
  }

  const getLoadingByContext = (context: string): LoadingState[] => {
    return Array.from(loadingStates.value.values()).filter(loading => loading.context === context)
  }

  // 统计信息
  const getStats = () => ({
    totalErrors: errors.value.size,
    criticalErrors: criticalErrors.value.length,
    networkErrors: networkErrors.value.length,
    retryableErrors: retryableErrors.value.length,
    activeLoading: loadingStates.value.size,
    isOnline: networkStatus.value.isOnline,
    lastOnlineTime: networkStatus.value.lastOnlineTime
  })

  // 初始化网络状态监听
  const initNetworkMonitoring = (): void => {
    // 监听在线/离线状态
    window.addEventListener('online', () => setOnlineStatus(true))
    window.addEventListener('offline', () => setOnlineStatus(false))

    // 监听网络连接变化
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection

    if (connection) {
      connection.addEventListener('change', () => {
        updateNetworkStatus({
          connectionType: connection.type,
          effectiveType: connection.effectiveType
        })
      })
    }

    // 定期检查网络状态
    setInterval(() => {
      if (navigator.onLine !== networkStatus.value.isOnline) {
        setOnlineStatus(navigator.onLine)
      }
    }, 5000)
  }

  // 清理资源
  const cleanup = (): void => {
    clearErrors()
    clearLoading()
    setGlobalLoading(false)
  }

  return {
    // 状态
    errors: computed(() => Array.from(errors.value.values())),
    loadingStates: computed(() => Array.from(loadingStates.value.values())),
    networkStatus: computed(() => networkStatus.value),
    globalLoading: computed(() => globalLoading.value),
    
    // 计算属性
    hasErrors,
    hasLoading,
    errorCount,
    loadingCount,
    criticalErrors,
    networkErrors,
    retryableErrors,
    
    // 错误处理方法
    addError,
    removeError,
    clearErrors,
    clearErrorsByType,
    clearErrorsByContext,
    retryError,
    retryAllErrors,
    
    // 加载状态方法
    startLoading,
    updateLoading,
    stopLoading,
    clearLoading,
    clearLoadingByContext,
    setGlobalLoading,
    
    // 网络状态方法
    updateNetworkStatus,
    setOnlineStatus,
    getConnectionInfo,
    
    // 工具方法
    getErrorById,
    getLoadingById,
    getErrorsByContext,
    getLoadingByContext,
    getStats,
    
    // 初始化和清理
    initNetworkMonitoring,
    cleanup
  }
})